import React from "react"
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { Table, TableHeader, TableBody, TableCell, TableRow, TableHead } from "@/components/ui/table"
import { tableHeadClass, tableCellClass } from "@/components/ui/table-styles"
import PaginationButton from "@/components/ui/pagination-button"
import { FileText } from "lucide-react"
import { InventoryLineItem, TransferHistoryStatus } from "@/gen/inventory/common_pb"
import dayjs from "dayjs"
import { Level, StockPointType } from "@/gen/auth/common_pb"
import { pharmacyClient } from "@/connect"
import { toast } from "@/components/ui/use-toast"
import { useRouter } from "@tanstack/react-router"

const Details = ({
    data,
    currentStatus,
    transferId,
    sourceBranchName,
    destinationBranchName,
    isCrossBranch
}: {
    data: InventoryLineItem[],
    currentStatus: TransferHistoryStatus,
    transferId: string,
    sourceBranchName: string,
    destinationBranchName: string,
    isCrossBranch: boolean
}) => {
    const [currentPage, setCurrentPage] = React.useState(1)
    const totalPages = Math.ceil(data.length / 5)
    const paginatedItems = data.slice((currentPage - 1) * 5, currentPage * 5)

    const level = localStorage.getItem('level')
    const stockPointType = localStorage.getItem('stockPointType')
    const router = useRouter()

    const getPageNumbers = () => {
        const pages = []
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i)
            }
        } else {
            pages.push(1)
            if (currentPage > 3) pages.push('...')
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(currentPage + 1, totalPages - 1); i++) {
                pages.push(i)
            }
            if (currentPage < totalPages - 2) pages.push('...')
            pages.push(totalPages)
        }
        return pages
    }

    return (
        <Dialog>
            <DialogTrigger>
                <Button variant="ghost" size="sm" className="flex items-center gap-1">
                    <FileText className="w-4 h-4" /> Details
                </Button>
            </DialogTrigger>
            <DialogContent className="w-[90vw] max-w-3xl p-0 flex flex-col gap-0">
                <DialogHeader className="p-4 border-b">
                    <DialogTitle>Transfer Details</DialogTitle>
                    <DialogDescription className="flex flex-col gap-1">
                        <span>Review the transfer details before approving or rejecting.</span>
                        <div className="flex items-center gap-2 text-sm font-medium mt-2">
                        <span className="text-gray-700">From:</span>
                        <span className="text-primary">{sourceBranchName || 'Unknown Branch'}</span>
                        <span className="text-gray-500">→</span>
                        <span className="text-gray-700">To:</span>
                        <span className="text-primary">{destinationBranchName || 'Unknown Branch'}</span>
                        {isCrossBranch && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            Cross-Branch Transfer
                            </span>
                        )}
                        </div>
                    </DialogDescription>
                </DialogHeader>
                <div className="flex-1 p-4 overflow-auto">
                    <div className="border border-slate-200 rounded-lg bg-white">
                        <Table className="text-sm">
                            <TableHeader>
                                <TableRow className="text-sm">
                                    <TableHead className={tableHeadClass}>SL No.</TableHead>
                                    <TableHead className={tableHeadClass}>Product Name</TableHead>
                                    <TableHead className={tableHeadClass}>Batch Number</TableHead>
                                    <TableHead className={tableHeadClass}>Expiry</TableHead>
                                    <TableHead className={tableHeadClass}>Quantity</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {paginatedItems.map((item, idx) => (
                                    <TableRow className="font-medium" key={item.inventoryId}>
                                        <TableCell className={tableCellClass}>{(currentPage - 1) * 10 + idx + 1}</TableCell>
                                        <TableCell className={tableCellClass}>{item.tradeName}</TableCell>
                                        <TableCell className={tableCellClass}>{item.batchNumber}</TableCell>
                                        <TableCell className={tableCellClass}>{dayjs(item.expiryDate?.toDate()).format("DD/MM/YYYY")}</TableCell>
                                        <TableCell className={tableCellClass}>{item.availableQuantity}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between p-2 text-sm border-t bg-white rounded-b-lg">
                                <p>{data.length} <span className="text-gray-500 font-light">count</span></p>
                                <div className="flex items-center gap-1">
                                    <PaginationButton onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                                        {'<<'}
                                    </PaginationButton>
                                    <PaginationButton onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                                        {'<'}
                                    </PaginationButton>
                                    {getPageNumbers().map((page, idx) => (
                                        page === '...'
                                            ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                                            : <PaginationButton
                                                key={page}
                                                active={currentPage === page}
                                                onClick={() => setCurrentPage(page as number)}
                                                disabled={currentPage === page}
                                            >
                                                <span className="text-sm">{page}</span>
                                            </PaginationButton>
                                    ))}
                                    <PaginationButton onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                                        {'>'}
                                    </PaginationButton>
                                    <PaginationButton onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                                        {'>>'}
                                    </PaginationButton>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {(level === Level.COMPANY.toString()|| level === Level.BRANCH.toString()) && (
                    <div className="flex justify-end gap-2 p-2 border-t">
                        {currentStatus === TransferHistoryStatus.PENDING_APPROVAL && (
                            <>
                                <Button variant="destructive" onClick={async () => {
                                    try {
                                        await pharmacyClient.changeTransferStatus({
                                            transferId: transferId,
                                            newStatus: TransferHistoryStatus.REJECTED
                                        })
                                        toast({
                                            title: "Success",
                                            description: "Transfer rejected successfully.",
                                            variant: "success",
                                            duration: 3000,
                                        })
                                    } catch (error) {
                                        console.error("Error rejecting transfer:", error)
                                        toast({
                                            title: "Error",
                                            description: "Failed to reject the transfer. Please try again.",
                                            variant: "destructive",
                                            duration: 3000,
                                        })
                                    } finally {
                                        router.invalidate()
                                    }
                                }}>Reject</Button>
                                <Button className="bg-blue-700 text-white hover:bg-blue-800" variant="default" onClick={async () => {
                                    try {
                                        await pharmacyClient.changeTransferStatus({
                                            transferId: transferId,
                                            newStatus: TransferHistoryStatus.APPROVED
                                        })
                                        toast({
                                            title: "Success",
                                            description: "Transfer approved successfully.",
                                            variant: "success",
                                            duration: 3000,
                                        })
                                    } catch (error) {
                                        console.error("Error approving transfer:", error)
                                        toast({
                                            title: "Error",
                                            description: "Failed to approve the transfer. Please try again.",
                                            variant: "destructive",
                                            duration: 3000,
                                        })
                                    } finally {
                                        router.invalidate()
                                    }
                                }}>Approve</Button>
                            </>
                        )}
                        {currentStatus === TransferHistoryStatus.APPROVED && (
                            <>
                                <Button className="bg-blue-700 text-white hover:bg-blue-800" variant="default" onClick={async () => {
                                    try {
                                        await pharmacyClient.changeTransferStatus({
                                            transferId: transferId,
                                            newStatus: TransferHistoryStatus.COMPLETED
                                        })
                                        toast({
                                            title: "Success",
                                            description: "Transfer completed successfully.",
                                            variant: "success",
                                            duration: 3000,
                                        })
                                    } catch (error) {
                                        console.error("Error completing transfer:", error)
                                        toast({
                                            title: "Error",
                                            description: "Failed to complete the transfer. Please try again.",
                                            variant: "destructive",
                                            duration: 3000,
                                        })
                                    } finally {
                                        router.invalidate()
                                    }
                                }}>Complete Transfer</Button>
                                <Button className="bg-red-700 text-white hover:bg-red-800" variant="default" onClick={async () => {
                                    try {
                                        await pharmacyClient.changeTransferStatus({
                                            transferId: transferId,
                                            newStatus: TransferHistoryStatus.CANCELLED
                                        })
                                        toast({
                                            title: "Success",
                                            description: "Transfer cancelled successfully.",
                                            variant: "success",
                                            duration: 3000,
                                        })
                                    } catch (error) {
                                        console.error("Error cancelling transfer:", error)
                                        toast({
                                            title: "Error",
                                            description: "Failed to cancel the transfer. Please try again.",
                                            variant: "destructive",
                                            duration: 3000,
                                        })
                                    } finally {
                                        router.invalidate()
                                    }
                                }}>Cancel Transfer</Button>
                            </>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default Details